package com.schoolconnect.app.feature.parentnotes

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.Image
import androidx.compose.ui.layout.ContentScale
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogDefaultColors
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSectionLabel
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppOutlinedButton
import com.schoolconnect.app.designsystem.AppButton
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.downloadFile
import com.schoolconnect.app.designsystem.list.DesktopAwareLazyColumn
import com.schoolconnect.app.datasource.network.response.ParentNoteReplyResponse
import com.schoolconnect.app.datasource.network.response.ParentNoteResponse
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.repository.files.appendMimeTypeExtension
import com.schoolconnect.app.util.resizeImage
import com.schoolconnect.app.util.decodeImageBitmap
import kotlinx.coroutines.launch
import io.github.vinceglb.filekit.compose.rememberFilePickerLauncher
import io.github.vinceglb.filekit.core.PickerMode
import io.github.vinceglb.filekit.core.PickerType
import io.github.vinceglb.filekit.core.PlatformFile
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.close_x
import ischoolconnect.sharedui.generated.resources.circle_x
import ischoolconnect.sharedui.generated.resources.document
import ischoolconnect.sharedui.generated.resources.download
import ischoolconnect.sharedui.generated.resources.message
import ischoolconnect.sharedui.generated.resources.paperclip
import ischoolconnect.sharedui.generated.resources.reply
import ischoolconnect.sharedui.generated.resources.arrow_right
import ischoolconnect.sharedui.generated.resources.user
import org.jetbrains.compose.resources.painterResource

private data class ConversationAttachmentUi(
    val file: PlatformFile,
    val name: String,
    val type: String,
    val sizeLabel: String,
    val fileId: String? = null,
    val previewBytes: ByteArray? = null
)

private data class ConversationHistoryAttachmentUi(
    val fileId: String,
    val url: String,
    val name: String,
    val mimeType: String,
    val sizeLabel: String,
    val previewBytes: ByteArray? = null
)

@Composable
internal fun ParentConversationDialog(
    row: ParentNoteRowUi?,
    detail: ParentNoteResponse? = null,
    replies: List<ParentNoteReplyResponse> = emptyList(),
    conversationInfo: ParentConversationInfo = ParentConversationInfo(),
    parentPreview: Boolean = false,
    allowReply: Boolean = true,
    isLoading: Boolean = false,
    replyResetGeneration: Int = 0,
    historyScrollGeneration: Int = 0,
    onDismiss: () -> Unit,
    onSendReply: (String, List<String>) -> Unit = { _, _ -> },
    onCloseConversation: () -> Unit = {}
) {
    if (row == null) return
    val isClosed = row.status.equals("Closed", ignoreCase = true) || detail?.status.equals("CLOSED", ignoreCase = true)
    val canReply = allowReply && !isClosed
    val canCloseConversation = !parentPreview && !isClosed
    var reply by remember(row.backendId.ifBlank { row.parentNoteId }) { mutableStateOf("") }
    var isUploadingFiles by remember(row.backendId.ifBlank { row.parentNoteId }) { mutableStateOf(false) }
    var attachmentError by remember(row.backendId.ifBlank { row.parentNoteId }) { mutableStateOf("") }
    var uploadSession by remember(row.backendId.ifBlank { row.parentNoteId }) { mutableStateOf(FileUploadRepository.newSession("PARENT_NOTE_REPLY")) }
    val historyListState = androidx.compose.foundation.lazy.rememberLazyListState()
    val uriHandler = LocalUriHandler.current
    var attachments by remember(row.backendId.ifBlank { row.parentNoteId }) { mutableStateOf(emptyList<ConversationAttachmentUi>()) }
    var historyAttachments by remember(row.backendId.ifBlank { row.parentNoteId }, detail?.id, replies) {
        mutableStateOf(emptyMap<String, ConversationHistoryAttachmentUi>())
    }
    LaunchedEffect(replyResetGeneration) {
        reply = ""
        attachments = emptyList()
        attachmentError = ""
        uploadSession = FileUploadRepository.newSession("PARENT_NOTE_REPLY")
    }
    LaunchedEffect(historyScrollGeneration) {
        if (historyScrollGeneration > 0 && replies.isNotEmpty()) {
            historyListState.animateScrollToItem(replies.size)
        }
    }
    LaunchedEffect(detail?.id, replies) {
        val refs = buildList {
            detail?.attachmentFileIds.orEmpty().forEachIndexed { index, id ->
                add(id to detail?.attachmentFileUrls.orEmpty().getOrNull(index).orEmpty())
            }
            replies.forEach { reply ->
                reply.attachmentFileIds.orEmpty().forEachIndexed { index, id ->
                    add(id to reply.attachmentFileUrls.orEmpty().getOrNull(index).orEmpty())
                }
            }
        }.filter { it.first.isNotBlank() || it.second.isNotBlank() }.distinctBy { it.first.ifBlank { it.second } }

        historyAttachments = refs.associate { (fileId, responseUrl) ->
            val resolvedFileId = fileId.ifBlank { responseUrl.substringBefore('?').substringAfterLast('/') }
            val metadata = resolvedFileId.takeIf { it.isNotBlank() }?.let { FileUploadRepository.getMetadata(it).getOrNull() }
            val mimeType = metadata?.detectedMimeType ?: metadata?.declaredMimeType ?: "application/octet-stream"
            val url = responseUrl.takeIf { it.startsWith("http://") || it.startsWith("https://") }
                ?: resolvedFileId.takeIf { it.isNotBlank() }?.let { FileUploadRepository.getDownloadUrl(it).getOrNull()?.url }
                ?: ""
            val previewBytes = if (mimeType.startsWith("image/", ignoreCase = true) && url.isNotBlank()) {
                FileUploadRepository.downloadBytes(url).getOrNull()
            } else null
            resolvedFileId to ConversationHistoryAttachmentUi(
                fileId = resolvedFileId,
                url = url,
                name = metadata?.originalFilename.orEmpty().ifBlank { "Attachment" },
                mimeType = mimeType,
                sizeLabel = metadata?.sizeBytes?.let(::formatConversationAttachmentSize) ?: "File",
                previewBytes = previewBytes
            )
        }
    }
    val coroutineScope = androidx.compose.runtime.rememberCoroutineScope()
    var downloadingAttachmentId by remember(row.backendId.ifBlank { row.parentNoteId }) { mutableStateOf<String?>(null) }
    fun downloadHistoryAttachment(attachment: ConversationHistoryAttachmentUi) {
        if (attachment.url.isBlank()) return
        coroutineScope.launch {
            downloadingAttachmentId = attachment.fileId
            try {
                FileUploadRepository.downloadBytes(attachment.url).onSuccess { bytes ->
                    downloadFile(appendMimeTypeExtension(attachment.name, attachment.mimeType), bytes, attachment.mimeType)
                }
            } finally {
                downloadingAttachmentId = null
            }
        }
    }
    val attachmentPicker = rememberFilePickerLauncher(
        type = PickerType.File(listOf("pdf", "doc", "docx", "jpg", "jpeg", "png")),
        mode = PickerMode.Multiple(),
        title = "Select Reply Attachments"
    ) { files ->
        if (files != null) coroutineScope.launch {
            isUploadingFiles = true
            attachmentError = ""
            for (file in files) {
                if (attachments.any { it.name.equals(file.name, ignoreCase = true) }) continue
                if ((file.getSize() ?: 0L) > 10L * 1024L * 1024L) {
                    attachmentError = "${file.name} is larger than 10 MB."
                    continue
                }
                val bytes = runCatching { file.readBytes() }.getOrNull()
                if (bytes == null || bytes.isEmpty()) { attachmentError = "Unable to read ${file.name}."; continue }
                val isImage = file.name.substringAfterLast('.', "").lowercase() in setOf("jpg", "jpeg", "png", "webp")
                val uploadBytes = if (isImage) resizeImage(bytes) else bytes
                val uploadFilename = if (isImage && uploadBytes !== bytes) "${file.name.substringBeforeLast('.', file.name)}.jpg" else file.name
                FileUploadRepository.upload(uploadSession, uploadFilename, conversationContentType(uploadFilename), uploadBytes).fold(
                    onSuccess = { uploaded ->
                        uploadSession = uploaded.first
                        attachments = attachments + ConversationAttachmentUi(file, file.name, file.name.substringAfterLast('.', "FILE").uppercase(), formatConversationAttachmentSize(file.getSize()), uploaded.second.fileId, if (isImage) uploadBytes else null)
                    },
                    onFailure = { throwable -> attachmentError = throwable.message ?: "Unable to upload ${file.name}." }
                )
            }
            isUploadingFiles = false
        }
    }

    AppDialogScaffold(onDismiss = onDismiss, size = com.schoolconnect.app.designsystem.AppDialogSize.Wide, dialogWidthOverride = 1240.dp, maxDialogHeight = 840.dp, viewportFraction = 0.995f, fillDialogHeight = true, isLoading = isLoading || isUploadingFiles, loadingTitle = if (isUploadingFiles) "Uploading attachments" else "Loading conversation") {
        ParentConversationHeader(row, parentPreview, conversationInfo, onDismiss)
        Column(
            Modifier.fillMaxWidth().weight(1f).padding(horizontal = 24.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            ParentConversationPanel {
                ParentConversationSectionHeader(if (parentPreview) "Conversation Information" else "Student Information", Res.drawable.user)
                if (parentPreview) {
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(20.dp)) {
                        ConversationValue("Student", row.studentName, Modifier.weight(1f))
                        ConversationValue("Class", "${row.className}-${row.section}", Modifier.weight(1f))
                        ConversationValue("Teacher", conversationInfo.teacherName.ifBlank { row.teacherName }.ifBlank { "Not available" }, Modifier.weight(1f))
                        ConversationValue("Last Activity", conversationInfo.lastActivity.toConversationTimestamp().ifBlank { row.date }.ifBlank { "Not available" }, Modifier.weight(1f))
                    }
                    AppDialogDivider()
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(20.dp)) {
                        ConversationValue("Parent", conversationInfo.parentName.ifBlank { "Not available" }, Modifier.weight(1f))
                        ConversationValue("Mobile", conversationInfo.mobile.ifBlank { "Not available" }, Modifier.weight(1f))
                        ConversationValue("Status", if (row.status == "Awaiting Your Reply") "Awaiting Teacher" else row.status, Modifier.weight(1f))
                        ConversationValue("Conversation ID", conversationInfo.conversationId.ifBlank { "—" }, Modifier.weight(1f))
                    }
                } else {
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(20.dp)) {
                        ConversationValue("Student", detail?.studentName.orEmpty().ifBlank { row.studentName }, Modifier.weight(1f))
                        ConversationValue("Class", listOf(detail?.className.orEmpty(), detail?.sectionName.orEmpty()).filter { it.isNotBlank() }.joinToString("-").ifBlank { "${row.className}-${row.section}" }, Modifier.weight(1f))
                        ConversationValue("Teacher", conversationInfo.teacherName.ifBlank { row.teacherName }.ifBlank { "Not available" }, Modifier.weight(1f))
                        ConversationValue("Last Activity", conversationInfo.lastActivity.toConversationTimestamp().ifBlank { detail?.updatedAt.toConversationTimestamp() }.ifBlank { "Not available" }, Modifier.weight(1f))
                    }
                    AppDialogDivider()
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(20.dp)) {
                        ConversationValue("Parent", conversationInfo.parentName.ifBlank { "Not available" }, Modifier.weight(1f))
                        ConversationValue("Mobile", conversationInfo.mobile.ifBlank { "Not available" }, Modifier.weight(1f))
                        ConversationValue("Status", detail?.status.toConversationStatus(row.status), Modifier.weight(1f))
                        conversationInfo.conversationId.takeIf(String::isNotBlank)?.let {
                            ConversationValue("Conversation ID", it, Modifier.weight(1f))
                        }
                    }
                }
            }
            ParentConversationPanel(Modifier.weight(1f)) {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    ParentConversationSectionHeader("Conversation History", Res.drawable.message)
                    Text("${replies.size + 1} ${if (replies.size + 1 == 1) "message" else "messages"}", color = AppMuted, fontSize = 12.sp)
                }
                DesktopAwareLazyColumn(
                    modifier = Modifier.fillMaxWidth().weight(1f),
                    state = historyListState,
                    contentPadding = PaddingValues(end = 12.dp),
                    verticalArrangement = Arrangement.spacedBy(0.dp)
                ) {
                    item(key = "original-note") {
                        Column {
                            ConversationMessage(
                                initials = (conversationInfo.teacherName.ifBlank { row.teacherName }).conversationInitials("T"),
                                sender = conversationInfo.teacherName.ifBlank { row.teacherName }.ifBlank { "Teacher" },
                                role = "Teacher",
                                timestamp = detail?.createdAt.toConversationTimestamp(),
                                title = detail?.title.orEmpty().ifBlank { row.title },
                                body = detail?.message.orEmpty().ifBlank { row.detail },
                                accent = AppPrimary,
                                attachmentIds = detail?.attachmentFileIds.orEmpty(),
                                attachmentUrls = detail?.attachmentFileUrls.orEmpty(),
                                attachmentDetails = historyAttachments,
                                downloadAttachment = ::downloadHistoryAttachment,
                                downloadingAttachmentId = downloadingAttachmentId,
                                openAttachment = { uri -> runCatching { uriHandler.openUri(uri) } }
                            )
                            if (replies.isNotEmpty()) AppDialogDivider()
                        }
                    }
                    itemsIndexed(replies, key = { index, reply -> reply.id ?: "reply-$index" }) { index, reply ->
                        val isParent = reply.senderType.equals("PARENT", true)
                        val roleFallback = if (isParent) "Parent" else "Teacher"
                        val senderFromResponse = reply.senderName.orEmpty().trim()
                        val sender = if (senderFromResponse.isBlank() || senderFromResponse.equals(roleFallback, true) || senderFromResponse.contains('@')) {
                            if (isParent) conversationInfo.parentName.ifBlank { roleFallback }
                            else conversationInfo.teacherName.ifBlank { row.teacherName }.ifBlank { roleFallback }
                        } else senderFromResponse
                        Column {
                            ConversationMessage(
                                initials = sender.conversationInitials(if (isParent) "P" else "T"),
                                sender = sender,
                                role = reply.senderType.toDisplayConversationRole(),
                                timestamp = reply.createdAt.toConversationTimestamp(),
                                title = "",
                                body = reply.message.orEmpty(),
                                accent = if (isParent) Color(0xFF16A34A) else AppPrimary,
                                attachmentIds = reply.attachmentFileIds.orEmpty(),
                                attachmentUrls = reply.attachmentFileUrls.orEmpty(),
                                attachmentDetails = historyAttachments,
                                downloadAttachment = ::downloadHistoryAttachment,
                                downloadingAttachmentId = downloadingAttachmentId,
                                openAttachment = { uri -> runCatching { uriHandler.openUri(uri) } }
                            )
                            if (index < replies.lastIndex) AppDialogDivider()
                        }
                    }
                }
            }
            if (canReply) {
                ParentConversationPanel {
                    ParentConversationSectionHeader(if (parentPreview) "Add Reply" else "Reply", Res.drawable.reply)
                    AppDialogTextField(reply, { reply = it }, "Type your reply here...", colors = AppDialogDefaultColors, singleLine = false, fieldHeight = 64.dp, maxLength = 2000)
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        AppOutlinedButton(onClick = { attachmentPicker.launch() }, modifier = Modifier.height(40.dp), contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 12.dp), colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary), shape = RoundedCornerShape(8.dp)) {
                            Icon(painterResource(Res.drawable.paperclip), null, tint = AppPrimary, modifier = Modifier.size(18.dp)); Spacer(Modifier.width(6.dp)); Text("Attach File", color = AppPrimary)
                        }
                        Text("Supported: PDF, DOC, DOCX, JPG, PNG · Max 10 MB per file", color = AppMuted, fontSize = 12.sp)
                    }
                    attachments.chunked(2).forEach { attachmentRow ->
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                            attachmentRow.forEach { attachment ->
                                ConversationAttachmentChip(attachment, Modifier.weight(1f)) {
                                    attachments = attachments - attachment
                                    attachment.fileId?.let { fileId -> coroutineScope.launch { FileUploadRepository.delete(fileId) } }
                                }
                            }
                            if (attachmentRow.size == 1) Spacer(Modifier.weight(1f))
                        }
                    }
                    if (attachmentError.isNotBlank()) Text(attachmentError, color = Color(0xFFD32F2F), fontSize = 12.sp)
                }
            }
            Spacer(Modifier.height(2.dp))
        }
        AppDialogDivider()
        ParentConversationFooter(onDismiss = onDismiss, onSendReply = { onSendReply(reply, attachments.mapNotNull { it.fileId }) }, onCloseConversation = onCloseConversation, showReply = canReply, showClose = canCloseConversation, enabled = !isUploadingFiles)
    }
}


@Composable
private fun ParentConversationHeader(row: ParentNoteRowUi, parentPreview: Boolean, conversationInfo: ParentConversationInfo, onDismiss: () -> Unit) {
    Row(
        Modifier.fillMaxWidth().padding(start = 24.dp, end = 20.dp, top = 20.dp, bottom = 14.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top
    ) {
        Row(Modifier.weight(1f), horizontalArrangement = Arrangement.spacedBy(16.dp), verticalAlignment = Alignment.Top) {
            Box(Modifier.size(44.dp).border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(10.dp)), contentAlignment = Alignment.Center) {
                Icon(painterResource(Res.drawable.message), null, tint = AppPrimary, modifier = Modifier.size(24.dp))
            }
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text("Parent Conversation", color = AppNavy, fontSize = 24.sp, fontWeight = FontWeight.ExtraBold)
                Text(if (parentPreview) "Communicate with your child's teacher." else "Communicate with parents about students.", color = AppMuted, fontSize = 14.sp)
            }
        }
        if (parentPreview) {
            Text(conversationInfo.conversationId.ifBlank { "—" }, color = AppNavy, fontSize = 16.sp, fontWeight = FontWeight.ExtraBold)
        } else {
            Column(horizontalAlignment = Alignment.End, verticalArrangement = Arrangement.spacedBy(5.dp)) {
                Row(horizontalArrangement = Arrangement.spacedBy(5.dp)) {
                    Text("Status :", color = AppNavy, fontSize = 13.sp)
                    Text(row.status, color = parentNoteStatusColor(row.status), fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                }
            }
        }
        Icon(painterResource(Res.drawable.close_x), "Close dialog", tint = AppNavy, modifier = Modifier.padding(start = 18.dp).size(24.dp).clickable(onClick = onDismiss))
    }
}

@Composable
private fun ParentConversationFooter(onDismiss: () -> Unit, onSendReply: () -> Unit, onCloseConversation: () -> Unit, showReply: Boolean, showClose: Boolean, enabled: Boolean) {
    Row(
        Modifier.fillMaxWidth().height(76.dp).padding(horizontal = 24.dp),
        horizontalArrangement = Arrangement.End,
        verticalAlignment = Alignment.CenterVertically
    ) {
        AppOutlinedButton(onClick = onDismiss, modifier = Modifier.width(120.dp).height(48.dp), shape = RoundedCornerShape(8.dp), colors = ButtonDefaults.outlinedButtonColors(contentColor = AppNavy)) { Text("Cancel", color = AppNavy, fontWeight = FontWeight.ExtraBold) }
        if (showClose) {
            Spacer(Modifier.width(14.dp))
            OutlinedButton(onClick = onCloseConversation, modifier = Modifier.width(220.dp).height(48.dp), shape = RoundedCornerShape(8.dp), colors = ButtonDefaults.outlinedButtonColors(contentColor = Color(0xFFFF303D)), border = BorderStroke(1.dp, Color(0xFFFF303D))) {
                Text("Close Conversation", color = Color(0xFFFF303D), fontWeight = FontWeight.ExtraBold)
            }
        }
        if (showReply) {
            Spacer(Modifier.width(14.dp))
            AppButton(onClick = onSendReply, enabled = enabled, modifier = Modifier.width(190.dp).height(48.dp), shape = RoundedCornerShape(8.dp), colors = ButtonDefaults.buttonColors(containerColor = AppPrimary, contentColor = Color.White)) {
                Text("Send Reply", fontWeight = FontWeight.ExtraBold)
            }
        }
    }
}

@Composable
private fun ParentConversationPanel(modifier: Modifier = Modifier, content: @Composable ColumnScope.() -> Unit) {
    Column(modifier.fillMaxWidth().border(BorderStroke(1.dp, AppDialogDefaultColors.border), RoundedCornerShape(8.dp)).padding(10.dp), verticalArrangement = Arrangement.spacedBy(8.dp), content = content)
}

@Composable
private fun ParentConversationSectionHeader(title: String, icon: org.jetbrains.compose.resources.DrawableResource) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(9.dp)) {
        Icon(painterResource(icon), null, tint = AppPrimary, modifier = Modifier.size(21.dp))
        AppDialogSectionLabel(title, colors = AppDialogDefaultColors, fontSize = 17.sp)
    }
}

@Composable
private fun ConversationValue(label: String, value: String, modifier: Modifier) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(label, color = AppMuted, fontSize = 12.sp)
        Text(value, color = AppNavy, fontSize = 14.sp, fontWeight = FontWeight.ExtraBold, maxLines = 1)
    }
}

@Composable
private fun ConversationMessage(
    initials: String,
    sender: String,
    role: String,
    timestamp: String,
    title: String,
    body: String,
    accent: Color,
    attachmentIds: List<String>,
    attachmentUrls: List<String>,
    attachmentDetails: Map<String, ConversationHistoryAttachmentUi>,
    downloadAttachment: (ConversationHistoryAttachmentUi) -> Unit,
    downloadingAttachmentId: String?,
    openAttachment: (String) -> Unit
) {
    Row(Modifier.fillMaxWidth().padding(vertical = 12.dp), horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.Top) {
        Box(
            Modifier.size(42.dp).background(accent.copy(alpha = .08f), RoundedCornerShape(50.dp)).border(1.dp, accent.copy(alpha = .2f), RoundedCornerShape(50.dp)),
            contentAlignment = Alignment.Center
        ) {
            Text(initials, color = accent, fontWeight = FontWeight.Bold, fontSize = 13.sp)
        }
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(5.dp)) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Top) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                    Text(sender, color = AppNavy, fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
                    Text("($role)", color = AppMuted, fontSize = 12.sp)
                }
                Text(timestamp, color = AppMuted, fontSize = 11.sp)
            }
            if (title.isNotBlank()) Text(title, color = AppNavy, fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
            if (body.isNotBlank()) Text(body, color = AppNavy, fontSize = 13.sp, lineHeight = 20.sp)
            val refs = attachmentIds.mapIndexed { index, id -> id to attachmentUrls.getOrNull(index).orEmpty() }
                .ifEmpty { attachmentUrls.map { "" to it } }
            refs.filter { it.first.isNotBlank() || it.second.isNotBlank() }.forEach { (id, responseUrl) ->
                val key = id.ifBlank { responseUrl.substringBefore('?').substringAfterLast('/') }
                val attachment = attachmentDetails[key]
                if (attachment != null) {
                    ConversationHistoryAttachmentRow(attachment, downloadingAttachmentId == attachment.fileId, { downloadAttachment(attachment) })
                } else {
                    Row(Modifier.clickable { openAttachment(responseUrl) }, verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                        Icon(painterResource(Res.drawable.paperclip), "Open attachment", tint = AppPrimary, modifier = Modifier.size(16.dp))
                        Text(responseUrl.conversationAttachmentName(), color = AppPrimary, fontSize = 12.sp, fontWeight = FontWeight.Medium)
                    }
                }
            }
        }
    }
}

@Composable
private fun ConversationHistoryAttachmentRow(attachment: ConversationHistoryAttachmentUi, isDownloading: Boolean, downloadAttachment: () -> Unit) {
    val image = remember(attachment.fileId, attachment.previewBytes) { attachment.previewBytes?.let(::decodeImageBitmap) }
    val kind = when {
        attachment.mimeType.startsWith("image/", ignoreCase = true) -> "Image"
        attachment.mimeType.contains("pdf", ignoreCase = true) -> "PDF"
        else -> attachment.name.substringAfterLast('.', "FILE").uppercase().ifBlank { "FILE" }
    }
    Row(Modifier.fillMaxWidth().border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(8.dp)).padding(8.dp), verticalAlignment = Alignment.CenterVertically) {
        if (image != null) Image(image, attachment.name, Modifier.size(52.dp).background(AppDialogDefaultColors.surface, RoundedCornerShape(6.dp)), contentScale = ContentScale.Crop)
        else Box(Modifier.size(34.dp).background(Color(0xFFEAF0FF), RoundedCornerShape(6.dp)), contentAlignment = Alignment.Center) { Icon(painterResource(Res.drawable.document), attachment.name, tint = AppPrimary, modifier = Modifier.size(20.dp)) }
        Spacer(Modifier.width(10.dp))
        Column(Modifier.weight(1f)) {
            Text(attachment.name, color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.Medium, maxLines = 1)
            Text("$kind  •  ${attachment.sizeLabel}", color = AppMuted, fontSize = 11.sp)
        }
        if (isDownloading) {
            CircularProgressIndicator(color = AppPrimary, strokeWidth = 2.dp, modifier = Modifier.size(20.dp))
        } else {
            Icon(painterResource(Res.drawable.download), "Download ${attachment.name}", tint = if (attachment.url.isNotBlank()) AppPrimary else AppMuted, modifier = Modifier.size(20.dp).then(if (attachment.url.isBlank()) Modifier else Modifier.clickable(onClick = downloadAttachment)))
        }
    }
}

@Composable
private fun ConversationAttachmentChip(attachment: ConversationAttachmentUi, modifier: Modifier, onRemove: () -> Unit) {
    Row(modifier.height(52.dp).border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(8.dp)).padding(horizontal = 10.dp), verticalAlignment = Alignment.CenterVertically) {
        val preview = remember(attachment.fileId, attachment.previewBytes) { attachment.previewBytes?.let(::decodeImageBitmap) }
        if (preview != null) {
            Image(preview, attachment.name, Modifier.size(36.dp).background(AppDialogDefaultColors.surface, RoundedCornerShape(6.dp)), contentScale = ContentScale.Crop)
        } else {
            Icon(painterResource(Res.drawable.document), null, tint = AppPrimary, modifier = Modifier.size(20.dp))
        }
        Spacer(Modifier.width(8.dp))
        Column(Modifier.weight(1f)) {
            Text(attachment.name, color = AppNavy, fontSize = 12.sp, maxLines = 1)
            Text("${attachment.type}  •  ${attachment.sizeLabel}", color = AppMuted, fontSize = 11.sp)
        }
        Icon(painterResource(Res.drawable.close_x), "Remove ${attachment.name}", tint = AppNavy, modifier = Modifier.size(18.dp).clickable(onClick = onRemove))
    }
}

private fun formatConversationAttachmentSize(bytes: Long?): String {
    if (bytes == null || bytes <= 0L) return "Size unavailable"
    return if (bytes >= 1024L * 1024L) "${bytes / (1024L * 1024L)} MB" else "${(bytes / 1024L).coerceAtLeast(1L)} KB"
}

private fun conversationContentType(filename: String): String = when (filename.substringAfterLast('.', "").lowercase()) {
    "pdf" -> "application/pdf"
    "doc" -> "application/msword"
    "docx" -> "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    "jpg", "jpeg" -> "image/jpeg"
    "png" -> "image/png"
    else -> "application/octet-stream"
}

private fun String?.toConversationTimestamp(): String {
    val value = this.orEmpty().trim().replace('T', ' ').substringBefore('+').substringBefore('Z')
    if (value.isBlank()) return "—"
    val date = value.substringBefore(' ').split('-')
    val time = value.substringAfter(' ', "").split(':')
    if (date.size != 3 || date[0].length != 4) return value
    val month = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")[date[1].toIntOrNull()?.minus(1)?.coerceIn(0, 11) ?: 0]
    if (time.size < 2) return "${date[2].padStart(2, '0')} $month ${date[0]}"
    val hour24 = time[0].toIntOrNull()?.coerceIn(0, 23) ?: 0
    val minute = time[1].toIntOrNull()?.coerceIn(0, 59) ?: 0
    val period = if (hour24 >= 12) "PM" else "AM"
    val hour12 = when (val hour = hour24 % 12) { 0 -> 12; else -> hour }
    return "${date[2].padStart(2, '0')} $month ${date[0]}, ${hour12}:${minute.toString().padStart(2, '0')} $period"
}

private fun String.conversationAttachmentName(): String =
    substringBefore('?').substringAfterLast('/').ifBlank { "Attachment" }

private fun String?.toConversationStatus(fallback: String): String =
    com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.label("PARENT_NOTE_STATUS", this.orEmpty().ifBlank { fallback })

private fun String.conversationInitials(fallback: String): String = trim().split(Regex("\\s+")).take(2).joinToString("") { it.firstOrNull()?.uppercase().orEmpty() }.ifBlank { fallback }

private fun String?.toDisplayConversationRole(): String = when (this.orEmpty().uppercase()) {
    "PARENT" -> "Parent"
    "TEACHER" -> "Teacher"
    "SCHOOL_ADMIN", "SCHOOL_ADMINISTRATOR" -> "School admin"
    "" -> "Reply"
    else -> this.orEmpty().lowercase().split('_').joinToString(" ") { part -> part.replaceFirstChar { it.uppercase() } }
}
